/**
 * Bulk Import — Almacén · «Importar» (design B): three steps.
 * 1 Archivo: the .xlsx or .csv (drag & drop or choose) and the template.
 * 2 Revisar: what each row creates, read by the server (server/security/warehouse-import.js).
 * 3 Asignar: the items are created without formations; go to «Asignar» with them marked.
 */

const BulkImport = {
    _step: 1,
    _file: null,     // { name, base64 }
    _rows: null,     // rows from the server preview
    _created: [],    // items created in step 3
    _error: '',
    _busy: false,

    render() {
        if (!AuthManager.canManageWarehouse()) return '<p class="text-center p-4">Acceso denegado</p>';
        const steps = [['Archivo', 'Sube el Excel o CSV'], ['Revisar', 'Qué se crea y qué no'], ['Asignar', 'Quién puede pedirlo']];
        const reachable = n => n === 1 ? !this._busy : n === 2 ? !!this._rows && !this._created.length && !this._busy : this._created.length > 0;
        return `
            <div class="whi-view">
                <ol class="whi-steps">
                    ${steps.map(([title, sub], i) => {
                        const n = i + 1, on = n === this._step, done = n < this._step;
                        return `<li><button type="button" class="whi-step ${on ? 'is-on' : ''} ${done ? 'is-done' : ''}" ${on ? 'aria-current="step"' : ''} ${reachable(n) ? '' : 'disabled'}
                            ${UIEvents.attr("click", function(event,captured){BulkImport.goTo(Number(captured[0]));}, [n])}>
                            <span class="whi-step-num">${done ? '<i class="fas fa-check" aria-hidden="true"></i>' : n}</span>
                            <span class="whi-step-text"><b>${title}</b><small>${sub}</small></span></button></li>`;
                    }).join('')}
                </ol>
                ${this._step === 1 ? this.renderFile() : this._step === 2 ? this.renderReview() : this.renderDone()}
            </div>`;
    },

    paint() {
        const box = document.getElementById('warehouse-import-container');
        if (box) SafeHTML.set(box, this.render());
    },

    goTo(step) {
        if (step === 1) { this._step = 1; this._created = []; }
        else if (step === 2 && this._rows && !this._created.length) this._step = 2;
        else if (step === 3 && this._created.length) this._step = 3;
        this.paint();
    },

    renderFile() {
        const columns = ['Nombre (obligatorio)', 'Cantidad (si falta, 1)', 'Categoría (por su nombre: Cámara, Iluminación…)', 'N.º de serie', 'Descripción'];
        return `
            <section class="wh-card whi-file">
                <label class="whi-drop ${this._busy ? 'is-busy' : ''}" id="whi-drop"
                    ${UIEvents.attr("dragover", function(event,captured){event.preventDefault();this.classList.add('is-over');}, [])}
                    ${UIEvents.attr("dragleave", function(event,captured){this.classList.remove('is-over');}, [])}
                    ${UIEvents.attr("drop", function(event,captured){event.preventDefault();this.classList.remove('is-over');BulkImport.readFile(event.dataTransfer && event.dataTransfer.files[0]);}, [])}>
                    <i class="fas fa-file-arrow-up" aria-hidden="true"></i>
                    <b>${this._busy ? 'Leyendo el archivo…' : 'Arrastra aquí el Excel del inventario'}</b>
                    <span>.xlsx o .csv · o</span>
                    <span class="whi-btn is-primary">Elegir archivo</span>
                    <input type="file" id="whi-input" class="whi-hidden" accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                        ${UIEvents.attr("change", function(event,captured){BulkImport.readFile(this.files[0]);}, [])}>
                </label>
                <div class="whi-help">
                    <span class="whi-label">Columnas que se leen (por su título, en cualquier orden)</span>
                    <ol>${columns.map(c => `<li>${c}</li>`).join('')}</ol>
                    <button type="button" class="whi-btn" ${UIEvents.attr("click", function(event,captured){PrivateDownloads.download('/api/warehouse/inventory/import/template');}, [])}><i class="fas fa-download" aria-hidden="true"></i> Descargar plantilla Excel</button>
                    <p class="whi-muted">Antes de crear nada verás cada fila: lo que se crea, lo que ya está en el inventario (mismo n.º de serie) y lo que tiene errores.</p>
                    ${this._error ? `<p class="whi-error" role="alert">${Utils.sanitizeHTML(this._error)}</p>` : ''}
                </div>
            </section>`;
    },

    readFileBase64(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
            reader.onerror = () => reject(new Error('No se pudo leer el archivo.'));
            reader.readAsDataURL(file);
        });
    },

    async readFile(file) {
        if (!file || this._busy) return;
        this._error = '';
        if (!/\.(xlsx|csv)$/i.test(file.name || '')) { this._error = 'Sube un archivo .xlsx o .csv (si es un .xls antiguo, guárdalo como .xlsx).'; this.paint(); return; }
        if (file.size > 3 * 1024 * 1024) { this._error = 'El archivo ocupa más de 3 MB.'; this.paint(); return; }
        this._busy = true;
        this.paint();
        try {
            const base64 = await this.readFileBase64(file);
            const result = await InventoryService.previewImport(base64);
            if (!result.success) throw new Error(result.message);
            this._file = { name: file.name, base64 };
            this._rows = result.rows;
            this._created = [];
            this._step = 2;
        } catch (e) {
            this._error = e.message || 'No se pudo leer el archivo.';
        } finally {
            this._busy = false;
            this.paint();
        }
    },

    _valid() { return (this._rows || []).filter(r => !r.errors.length && !r.duplicate); },

    renderReview() {
        const rows = this._rows || [], valid = this._valid();
        const warned = valid.filter(r => r.warnings.length).length, dup = rows.filter(r => !r.errors.length && r.duplicate).length, bad = rows.filter(r => r.errors.length).length;
        const cat = id => InventoryService.categoryLabel(id);
        const state = r => r.errors.length ? `<span class="whi-state is-bad"><i class="fas fa-xmark" aria-hidden="true"></i> ${Utils.sanitizeHTML(r.errors.join('. '))}</span>`
            : r.duplicate ? `<span class="whi-state is-skip">${Utils.sanitizeHTML(r.duplicate)}: se salta</span>`
            : r.warnings.length ? `<span class="whi-state is-warn"><i class="fas fa-triangle-exclamation" aria-hidden="true"></i> Nuevo · ${Utils.sanitizeHTML(r.warnings.join('. '))}</span>`
            : '<span class="whi-state is-ok"><i class="fas fa-check" aria-hidden="true"></i> Nuevo</span>';
        return `
            <section class="wh-card whi-review">
                <div class="whi-review-head">
                    <b class="whi-filename">${Utils.sanitizeHTML(this._file?.name || '')} · ${rows.length} ${rows.length === 1 ? 'fila' : 'filas'}</b>
                    <span class="whi-pill is-ok">${valid.length} ${valid.length === 1 ? 'nuevo' : 'nuevos'}</span>
                    ${warned ? `<span class="whi-pill is-warn">${warned} con aviso</span>` : ''}
                    ${dup ? `<span class="whi-pill is-skip">${dup} ya ${dup === 1 ? 'existe' : 'existen'}</span>` : ''}
                    ${bad ? `<span class="whi-pill is-bad">${bad} con error</span>` : ''}
                </div>
                <div class="whi-table-wrap">
                    <table class="whi-table">
                        <thead><tr><th>Fila</th><th>Nombre</th><th>Uds.</th><th>Categoría</th><th>N.º de serie</th><th>Estado</th></tr></thead>
                        <tbody>${rows.map(r => `<tr class="${r.errors.length ? 'is-bad' : r.duplicate ? 'is-skip' : r.warnings.length ? 'is-warn' : ''}">
                            <td>${Number(r.row)}</td><td><b>${Utils.sanitizeHTML(r.data.name || '—')}</b></td><td>${Number(r.data.quantity)}</td>
                            <td>${Utils.sanitizeHTML(cat(r.data.category))}</td><td>${Utils.sanitizeHTML(r.data.serialNumber || '—')}</td><td>${state(r)}</td></tr>`).join('')}</tbody>
                    </table>
                </div>
                <div class="whi-actions">
                    <button type="button" class="whi-btn" ${UIEvents.attr("click", function(event,captured){BulkImport.goTo(1);}, [])}>Cambiar archivo</button>
                    <button type="button" class="whi-btn is-primary" id="whi-create" ${valid.length && !this._busy ? '' : 'disabled'} ${UIEvents.attr("click", function(event,captured){BulkImport.create();}, [])}>
                        ${this._busy ? 'Creando…' : valid.length ? `Crear ${valid.length} ${valid.length === 1 ? 'elemento' : 'elementos'}` : 'No hay nada que crear'}</button>
                </div>
            </section>`;
    },

    async create() {
        const valid = this._valid();
        if (!valid.length || this._busy) return;
        this._busy = true;
        this.paint();
        const created = await InventoryService.importItems(valid.map(r => ({ ...r.data })));
        this._busy = false;
        if (created) {
            this._created = created;
            this._step = 3;
            Utils.showToast(`${created.length} ${created.length === 1 ? 'elemento creado' : 'elementos creados'}`, 'success');
        }
        this.paint();
    },

    renderDone() {
        const n = this._created.length;
        return `
            <section class="wh-card whi-done">
                <span class="whi-pill is-ok"><i class="fas fa-check" aria-hidden="true"></i> ${n} ${n === 1 ? 'elemento creado' : 'elementos creados'}</span>
                <h2>¿Quién puede pedirlos?</h2>
                <p>Hasta que los asignes a alguna formación, ningún alumno los ve. Puedes hacerlo ahora o más tarde en «Asignar».</p>
                <div class="whi-actions is-start">
                    <button type="button" class="whi-btn is-primary" ${UIEvents.attr("click", function(event,captured){BulkImport.assignNow();}, [])}>Asignar ${n === 1 ? 'el elemento' : `los ${n}`} ahora</button>
                    <button type="button" class="whi-btn" ${UIEvents.attr("click", function(event,captured){WarehouseView.switchTab('inventory');}, [])}>Ver en el inventario</button>
                    <button type="button" class="whi-btn" ${UIEvents.attr("click", function(event,captured){BulkImport.restart();}, [])}>Importar otro archivo</button>
                </div>
            </section>`;
    },

    assignNow() {
        const ids = this._created.map(i => String(i.id));
        this.restart(false);
        if (typeof AssignmentManager !== 'undefined') AssignmentManager.preselect(ids);
        WarehouseView.switchTab('assignment');
    },

    restart(repaint = true) {
        this._step = 1; this._file = null; this._rows = null; this._created = []; this._error = '';
        if (repaint) this.paint();
    }
};

window.BulkImport = BulkImport;
